/**
 * Row times and day groups for the Assistant sidebar's History section
 * (task 1202).
 *
 * Both answer in the browser's LOCAL time: "Today" is the User's today. Kept
 * dependency-free; `now` is a parameter so the branches can be tested against a
 * fixed clock.
 */

export type ThreadDayGroup = 'Today' | 'Yesterday' | 'Earlier';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const pad = (value: number) => String(value).padStart(2, '0');

/** Whole calendar days from `then` to `now`, in local time (0 = same day). */
function calendarDaysBetween(then: Date, now: Date): number {
  const startOf = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  return Math.round((startOf(now) - startOf(then)) / 86_400_000);
}

const parse = (lastActiveAt: string): Date | null => {
  const date = new Date(lastActiveAt);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * "just now", "N min ago", "N h ago" (earlier today), "Yesterday, HH:MM", or a
 * short date ("Oct 9", with the year when it is not this year). An unreadable
 * timestamp answers an empty string rather than "Invalid Date".
 */
export function relativeThreadTime(lastActiveAt: string, now: Date = new Date()): string {
  const then = parse(lastActiveAt);
  if (!then) return '';

  const minutes = Math.floor((now.getTime() - then.getTime()) / 60_000);
  // A timestamp a little ahead of this clock (skew) is still "just now".
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;

  const days = calendarDaysBetween(then, now);
  if (days === 0) return `${Math.floor(minutes / 60)} h ago`;
  if (days === 1) return `Yesterday, ${pad(then.getHours())}:${pad(then.getMinutes())}`;

  const short = `${MONTHS[then.getMonth()]} ${then.getDate()}`;
  return then.getFullYear() === now.getFullYear() ? short : `${short}, ${then.getFullYear()}`;
}

/** Which day label a row sits under; unreadable timestamps go under Earlier. */
export function threadDayGroup(lastActiveAt: string, now: Date = new Date()): ThreadDayGroup {
  const then = parse(lastActiveAt);
  if (!then) return 'Earlier';
  const days = calendarDaysBetween(then, now);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return 'Earlier';
}
